/* global gdAlertify */
import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../config/axiosConfig";

const HORAS_DEFAULT = 12;

const hoyISO = () => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 10);
};
const formatearFecha = (iso) => (iso ? iso.slice(0, 10).split("-").reverse().join("-") : "");
const NOMBRE_DIA = { 1: "Lun", 2: "Mar", 3: "Mié", 4: "Jue", 5: "Vie", 6: "Sáb", 7: "Dom" };

const DIAS = [
    { value: 1, label: "Lunes" },
    { value: 2, label: "Martes" },
    { value: 3, label: "Miércoles" },
    { value: 4, label: "Jueves" },
    { value: 5, label: "Viernes" },
    { value: 6, label: "Sábado" },
    { value: 7, label: "Domingo" },
];

const buildGrillaDefault = () => {
    const grilla = {};
    DIAS.forEach(dia => {
        grilla[`${dia.value}_day`] = HORAS_DEFAULT;
        grilla[`${dia.value}_night`] = HORAS_DEFAULT;
    });
    return grilla;
};

const HorasTurnoModal = ({ isOpen, onClose, pacienteId, pacienteNombre, onSaved }) => {
    const [grilla, setGrilla] = useState(buildGrillaDefault());
    const [loading, setLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    // La configuración rige desde una fecha: los turnos anteriores mantienen la configuración
    // anterior (ej. meses que se cubrieron con adicionales no se recalculan).
    const [vigenteDesde, setVigenteDesde] = useState(hoyISO());
    const [vigenciaActual, setVigenciaActual] = useState(null);
    const [versiones, setVersiones] = useState([]);

    const fetchHoras = useCallback(async () => {
        if (!pacienteId) return;

        setLoading(true);
        try {
            const response = await axiosInstance.get(`/paciente/${pacienteId}/horas-turno`);
            const filas = response.data?.data || [];
            const nueva = buildGrillaDefault();
            filas.forEach(fila => {
                nueva[`${fila.dia_semana}_${fila.tipo_turno}`] = Number(fila.horas);
            });
            setGrilla(nueva);
            const vigencia = response.data?.vigente_desde || null;
            setVigenciaActual(vigencia);
            setVersiones(response.data?.versiones || []);
            // Por defecto: hoy, o la vigencia ya programada si es futura (para corregirla).
            setVigenteDesde(vigencia && vigencia > hoyISO() ? vigencia : hoyISO());
        } catch (error) {
            console.error("Error al cargar horas de turno:", error);
            setGrilla(buildGrillaDefault());
        } finally {
            setLoading(false);
        }
    }, [pacienteId]);

    useEffect(() => {
        if (isOpen && pacienteId) {
            fetchHoras();
        }
    }, [fetchHoras, isOpen, pacienteId]);

    const handleChange = (dia, tipo, value) => {
        setGrilla(prev => ({ ...prev, [`${dia}_${tipo}`]: value }));
    };

    const handleRestablecer = (dia, tipo) => {
        setGrilla(prev => ({ ...prev, [`${dia}_${tipo}`]: HORAS_DEFAULT }));
    };

    const hayExcepciones = DIAS.some(dia =>
        Number(grilla[`${dia.value}_day`]) !== HORAS_DEFAULT ||
        Number(grilla[`${dia.value}_night`]) !== HORAS_DEFAULT
    );

    const buildFilas = () => {
        const filas = [];
        for (const dia of DIAS) {
            for (const tipo of ["day", "night"]) {
                const horas = Number(grilla[`${dia.value}_${tipo}`]);
                if (!horas || horas <= 0) {
                    gdAlertify.notify(`Las horas de ${dia.label} deben ser mayores a 0.`, "error");
                    return null;
                }
                filas.push({ dia_semana: dia.value, tipo_turno: tipo, horas });
            }
        }
        return filas;
    };

    // Antes de guardar se confirma desde cuándo rige: la última configuración manda desde
    // su fecha en adelante y reemplaza las que estuvieran programadas desde esa fecha.
    const handleGuardar = () => {
        const filas = buildFilas();
        if (!filas) return;
        if (!vigenteDesde) {
            gdAlertify.notify("Indique desde qué fecha rige la configuración.", "error");
            return;
        }

        const reemplazadas = versiones.filter(v => v.vigente_desde >= vigenteDesde).map(v => formatearFecha(v.vigente_desde));
        const mensaje = `Esta configuración entra en vigencia el ${formatearFecha(vigenteDesde)}. `
            + `Desde esa fecha se actualizará el valor de los turnos extendidos registrados cuando el cálculo sea mayor al valor actual. `
            + `Los turnos anteriores a esa fecha mantienen la configuración anterior.`
            + (reemplazadas.length ? ` Reemplaza la configuración registrada desde: ${reemplazadas.join(", ")}.` : "");

        if (typeof gdAlertify.confirm === "function") {
            gdAlertify.confirm({
                title: "Confirmar vigencia",
                message: mensaje,
                onConfirm: () => guardar(filas),
            });
        } else if (window.confirm(mensaje)) {
            guardar(filas);
        }
    };

    const guardar = async (filas) => {
        setIsSubmitting(true);
        try {
            gdAlertify.showLoader();
            const response = await axiosInstance.post("/paciente/horas-turno/guardar", {
                pacienteID: pacienteId,
                filas,
                vigenteDesde,
            });

            if (response.data?.error) {
                gdAlertify.notify(response.data.message || "Error al guardar la configuración.", "error");
                return;
            }

            gdAlertify.notify("Configuración de horas guardada.", "success");
            if (onSaved) onSaved();
            onClose();
        } catch (error) {
            console.error("Error al guardar horas de turno:", error);
            gdAlertify.notify("Error al guardar la configuración.", "error");
        } finally {
            gdAlertify.hideLoader();
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }} tabIndex="-1">
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Horas por Turno{pacienteNombre ? ` - ${pacienteNombre}` : ""}</h5>
                        <button type="button" className="btn-close" onClick={onClose} disabled={isSubmitting}></button>
                    </div>
                    <div className="modal-body">
                        <p className="text-muted small mb-3">
                            Por defecto todos los turnos duran <strong>12 horas</strong>. Solo modifique un día si ese turno
                            se extiende (ej. sábado y domingo con turno de 15 horas). Esta duración se usa para calcular,
                            en la misma proporción, lo que se cobra a la familia y lo que se paga al auxiliar.
                        </p>

                        <div className="row g-2 align-items-end mb-3">
                            <div className="col-md-4">
                                <label className="form-label small fw-bold mb-1">Vigente desde</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={vigenteDesde}
                                    onChange={(e) => setVigenteDesde(e.target.value)}
                                    disabled={isSubmitting}
                                />
                            </div>
                            <div className="col-md-8 small text-muted">
                                Los turnos <strong>antes del {formatearFecha(vigenteDesde) || "—"}</strong> mantienen la configuración
                                anterior (no se recalculan). Desde esa fecha rige la que guarde aquí.
                                {vigenciaActual && <> Configuración actual vigente desde <strong>{formatearFecha(vigenciaActual)}</strong>.</>}
                            </div>
                        </div>

                        {loading ? (
                            <div className="text-center py-3">Cargando...</div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-sm table-bordered align-middle text-center mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="text-start">Día</th>
                                            <th>Día (turno)</th>
                                            <th>Noche (turno)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {DIAS.map(dia => (
                                            <tr key={dia.value}>
                                                <td className="text-start fw-bold">{dia.label}</td>
                                                {["day", "night"].map(tipo => {
                                                    const key = `${dia.value}_${tipo}`;
                                                    const esExcepcion = Number(grilla[key]) !== HORAS_DEFAULT;
                                                    return (
                                                        <td key={tipo}>
                                                            <div className="input-group input-group-sm justify-content-center">
                                                                <input
                                                                    type="number"
                                                                    className={`form-control text-center ${esExcepcion ? "border-success text-success fw-bold" : ""}`}
                                                                    style={{ maxWidth: 80 }}
                                                                    min={1}
                                                                    step={0.5}
                                                                    value={grilla[key]}
                                                                    onChange={(e) => handleChange(dia.value, tipo, e.target.value)}
                                                                    disabled={isSubmitting}
                                                                />
                                                                <span className="input-group-text">h</span>
                                                            </div>
                                                            {esExcepcion && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-link btn-sm p-0 mt-1"
                                                                    onClick={() => handleRestablecer(dia.value, tipo)}
                                                                    disabled={isSubmitting}
                                                                >
                                                                    Volver a 12h
                                                                </button>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {!loading && versiones.length > 0 && (
                            <div className="mt-3 small">
                                <div className="fw-bold text-muted mb-1">Historial</div>
                                <ul className="mb-0 ps-3">
                                    {(() => {
                                        // La versión vigente hoy es la última con fecha <= hoy; las posteriores están programadas.
                                        const hoy = hoyISO();
                                        const vigenteHoy = versiones.filter(v => v.vigente_desde <= hoy).map(v => v.vigente_desde).pop();
                                        return versiones.slice().reverse().map(v => {
                                            const estado = v.vigente_desde > hoy ? "Programada" : v.vigente_desde === vigenteHoy ? "Vigente" : null;
                                            return (
                                                <li key={v.vigente_desde} className={estado ? "" : "text-muted"}>
                                                    Desde <strong>{formatearFecha(v.vigente_desde)}</strong>
                                                    {estado && <span className={`badge ms-1 ${estado === "Vigente" ? "bg-success" : "bg-info text-dark"}`}>{estado}</span>}:{" "}
                                                    {v.excepciones.length === 0
                                                        ? "todos los turnos de 12 h"
                                                        : v.excepciones.map(e => `${NOMBRE_DIA[e.dia_semana]} ${e.tipo_turno === "day" ? "día" : "noche"} ${String(e.horas).replace(".", ",")} h`).join(" · ")}
                                                    {(v.usuario_nombre || v.fecha_registro) && (
                                                        <span className="text-muted">
                                                            {" "}— registrado{v.usuario_nombre ? ` por ${v.usuario_nombre}` : ""}
                                                            {v.fecha_registro ? ` el ${formatearFecha(String(v.fecha_registro).slice(0, 10))}` : ""}
                                                        </span>
                                                    )}
                                                </li>
                                            );
                                        });
                                    })()}
                                </ul>
                            </div>
                        )}

                        {!loading && !hayExcepciones && (
                            <div className="alert alert-info mt-3 mb-0 py-2 small">
                                Sin excepciones configuradas: todos los turnos de este paciente duran 12 horas.
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSubmitting}>
                            Cancelar
                        </button>
                        <button type="button" className="btn btn-primary" onClick={handleGuardar} disabled={isSubmitting || loading}>
                            <i className="bi bi-save me-2"></i>
                            Guardar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HorasTurnoModal;
